import React, { useEffect, useState } from "react";
import Select from "react-select";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "https://dashboard.lono.co.in/api";

// Custom styles for react-select (dark theme)
const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: "#1e1e1e",
    borderColor: "#333",
    borderRadius: "0.375rem",
    padding: "2px",
    boxShadow: "none",
    "&:hover": { borderColor: "#444" },
  }),
  menu: (provided) => ({ ...provided, backgroundColor: "#1e1e1e" }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#333"
      : state.isFocused
      ? "#2a2a2a"
      : "#1e1e1e",
    color: "white",
  }),
  singleValue: (provided) => ({ ...provided, color: "white" }),
  multiValue: (provided) => ({ ...provided, backgroundColor: "#333" }),
  multiValueLabel: (provided) => ({ ...provided, color: "white" }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "white",
    ":hover": { backgroundColor: "#444", color: "white" },
  }),
  placeholder: (provided) => ({ ...provided, color: "#888" }),
  input: (provided) => ({ ...provided, color: "white" }),
};

const AmountApprove = ({ formData, setFormData }) => {
  // Dropdown options
  const [bankCategoriesOptions, setBankCategoriesOptions] = useState([]);
  const [banksOptions, setBanksOptions] = useState([]);
  const [partnersOptions, setPartnersOptions] = useState([]);
  const [loanTypeOptions, setLoanTypeOptions] = useState([{ label: "All", value: "all" }]);

  // Helper to update formData
  const updateFormData = (key, value) =>
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));

  // ---------- Initial fetch: categories & loan types ----------
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const response = await axios.get(`${API_URL}/banking-partners`);
        if (response.data && response.data.success) {
          const raw = Array.isArray(response.data.data) ? response.data.data : [];

          // Unique categories
          const uniqueCategories = [...new Set(raw.map((p) => p.bankCategory).filter(Boolean))];

          const categoryOptions = [{ label: "All", value: "all" }, ...uniqueCategories.map((c) => ({ label: c, value: c }))];
          setBankCategoriesOptions(categoryOptions);

          // Loan types (if provided)
          if (response.data.availableLoanTypes) {
            const loanTypes = response.data.availableLoanTypes.map((type) => ({
              label: type === "all" ? "All" : String(type),
              value: type,
            }));
            setLoanTypeOptions(loanTypes);
          }
        }
      } catch (err) {
        console.error("Error fetching initial data:", err);
      }
    };

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- When categories or loanType changes -> fetch banks ----------
  useEffect(() => {
    const selectedCategoryValues = (formData?.selectedCategories || []).map((c) => c.value);
    if (selectedCategoryValues.length > 0) {
      fetchBanks(selectedCategoryValues);
    } else {
      // clear banks and partners if no categories selected
      setBanksOptions([]);
      updateFormData("selectedBanks", []);
      setPartnersOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.selectedCategories, formData?.selectedLoanType]);

  // ---------- When selectedBanks or categories or banksOptions change -> fetch reps ----------
  useEffect(() => {
    const selectedBankValues = (formData?.selectedBanks || []).map((b) => b.value).filter(Boolean);
    const selectedCategoryValues = (formData?.selectedCategories || []).map((c) => c.value).filter(Boolean);

    // Priority: if specific banks selected (non-helper) -> fetch reps for those banks
    const bankIdsFromSelection = selectedBankValues.filter((id) => id !== "all");
    if (bankIdsFromSelection.length > 0) {
      fetchRepresentativesForBankIds(bankIdsFromSelection);
      return;
    }

    // If no specific banks, but categories selected -> derive bank ids from banksOptions (those currently available)
    if (selectedCategoryValues.length > 0) {
      const bankIdsFromOptions = banksOptions.filter((opt) => opt && opt.value && opt.value !== "all").map((opt) => opt.value);
      if (bankIdsFromOptions.length > 0) {
        fetchRepresentativesForBankIds(bankIdsFromOptions);
        return;
      }
    }

    // Otherwise clear
    setPartnersOptions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.selectedBanks, formData?.selectedCategories, banksOptions]);

  // ---------- Fetch banks ----------
  const fetchBanks = async (categories = []) => {
    try {
      const loanType = formData?.selectedLoanType?.value || "all";
      const response = await axios.get(`${API_URL}/banking-partners`, { params: { loanType } });

      if (response.data && response.data.success) {
        let raw = Array.isArray(response.data.data) ? response.data.data : [];

        // If categories doesn't include 'all', filter by selected categories
        if (!categories.includes("all")) {
          raw = raw.filter((p) => categories.includes(p.bankCategory));
        }

        // Map unique banks and preserve branchName & category in label
        const mapped = raw
          .map((b) => {
            if (!b || !b._id || !b.bankName) return null;
            const branchPart = b.branchName ? ` - ${b.branchName}` : "";
            const categoryPart = b.bankCategory ? ` (${b.bankCategory})` : "";
            return { label: `${b.bankName}${categoryPart}${branchPart}`, value: b._id };
          })
          .filter(Boolean)
          .filter((bank, idx, arr) => idx === arr.findIndex((x) => x.value === bank.value));

        // Prepend helper 'All' option (helper will not be kept in selected state)
        setBanksOptions([{ label: "All", value: "all" }, ...mapped]);
      }
    } catch (err) {
      console.error("Error fetching banks:", err);
      setBanksOptions([]);
    }
  };

  // ---------- Fetch representatives for multiple bank IDs ----------
  const fetchRepresentativesForBankIds = async (bankIds = []) => {
    if (!bankIds || bankIds.length === 0) {
      setPartnersOptions([]);
      return;
    }

    try {
      // dedupe and stringify
      const uniqueIds = Array.from(new Set(bankIds.map(String).filter(Boolean)));

      // Prepare a map to get bank label (name) from banksOptions for better rep labels
      const bankLabelById = new Map(banksOptions.map((b) => [String(b.value), b.label]));

      // Fetch reps concurrently
      const requests = uniqueIds.map((id) => axios.get(`${API_URL}/banking-partners/representatives/${id}`));
      const results = await Promise.allSettled(requests);

      // Collect reps, dedupe by email
      const repByEmail = new Map();

      results.forEach((res, idx) => {
        if (res.status === "fulfilled" && res.value && res.value.data && res.value.data.success && Array.isArray(res.value.data.data)) {
          const reps = res.value.data.data;
          const bankId = uniqueIds[idx];
          const bankLabel = bankLabelById.get(String(bankId)) || "";

          reps.forEach((rep) => {
            if (!rep || !rep.email || !rep.name) return;
            const email = rep.email;
            // label shows rep name + designation + (bank label) for clarity
            const label = `${rep.name} (${rep.designation || "N/A"})${bankLabel ? ` — ${bankLabel}` : ""}`;

            if (!repByEmail.has(email)) {
              repByEmail.set(email, { label, value: email });
            }
          });
        }
      });

      const uniqueReps = Array.from(repByEmail.values());
      // Prepend helper "All"
      setPartnersOptions([{ label: "All", value: "all" }, ...uniqueReps]);
    } catch (err) {
      console.error("Error fetching representatives:", err);
      setPartnersOptions([]);
    }
  };

  // ---------- Generic handler for multi-selects with "All" helper ----------
  // optionsParam must be the full options array passed to the Select (including the helper 'All')
  const handleMultiSelect = (selected, optionsParam = [], formDataKey) => {
    if (!selected) {
      updateFormData(formDataKey, []);
      return;
    }

    const clickedAll = selected.some((s) => s.value === "all");

    if (clickedAll) {
      // select all actual options except the helper 'All'
      const withoutHelper = optionsParam.filter((opt) => opt.value !== "all");
      updateFormData(formDataKey, [...withoutHelper]);
    } else {
      updateFormData(formDataKey, selected);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 bg-[#0f0f0f] rounded-lg shadow-md text-white">
      <h3 className="text-md font-medium text-white mb-3">Approve Amount</h3>

      {/* Amount Type Toggle */}
      <div className="flex items-center gap-6 mb-4 text-sm">
        {["Requested Amount", "Custom Amount"].map((option, idx) => {
          const value = idx === 1 ? "custom" : "requested";
          return (
            <label key={option} className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="amountType"
                checked={formData?.amountType === value}
                onChange={() => updateFormData("amountType", value)}
                className="hidden"
              />
              <span
                className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${
                  formData?.amountType === value ? "border-yellow-400" : "border-gray-400"
                }`}
              >
                {formData?.amountType === value && <span className="w-2.5 h-2.5 bg-yellow-400 rounded-full" />}
              </span>
              <span className={formData?.amountType === value ? "text-yellow-400" : "text-gray-300"}>{option}</span>
            </label>
          );
        })}
      </div>

      {/* ₹ Input Field */}
      {formData?.amountType === "custom" && (
        <div className="relative mb-4">
          <span className="absolute left-3 top-2.5 text-gray-400">₹</span>
          <input
            type="number"
            value={formData?.customAmount || ""}
            onChange={(e) => updateFormData("customAmount", e.target.value)}
            placeholder="Enter Amount"
            className="w-full pl-8 pr-4 py-2 bg-[#1e1e1e] border border-[#333333] text-white rounded-md placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-yellow-400"
          />
        </div>
      )}

      {/* Processing Options */}
      <div className="mb-4">
        <h3 className="text-md font-medium text-white mb-2">Processing Options</h3>
        <div className="flex items-center gap-6 mb-4 text-sm">
          {["Internal", "External"].map((option, idx) => {
            const value = idx === 1 ? "external" : "internal";
            return (
              <label key={option} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="processingOption"
                  checked={formData?.processingOption === value}
                  onChange={() => {
                    updateFormData("processingOption", value);
                    // Reset related fields when switching
                    updateFormData("selectedCategories", []);
                    updateFormData("selectedBanks", []);
                    updateFormData("selectedPartners", []);
                    // default bank name for internal
                    updateFormData("bankName", value === "internal" ? "Lono Micro Finance Bank" : "");
                    setBanksOptions([]);
                    setPartnersOptions([]);
                  }}
                  className="hidden"
                />
                <span
                  className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${
                    formData?.processingOption === value ? "border-yellow-400" : "border-gray-400"
                  }`}
                >
                  {formData?.processingOption === value && <span className="w-2.5 h-2.5 bg-yellow-400 rounded-full" />}
                </span>
                <span className={formData?.processingOption === value ? "text-yellow-400" : "text-gray-300"}>{option}</span>
              </label>
            );
          })}
        </div>

        {formData?.processingOption === "external" ? (
          <div className="space-y-4">
            {/* Loan Type (read-only display) */}
            <div>
              <label className="block text-sm text-gray-300 mb-1">Loan Type</label>
              <input
                type="text"
                value={formData?.selectedLoanType?.label || formData?.selectedLoanType?.value || ""}
                disabled
                className="w-full px-3 py-2 bg-[#1e1e1e] border border-[#333333] text-white rounded-md placeholder-gray-500 cursor-not-allowed"
              />
            </div>

            {/* Bank Category */}
            <div>
              <label className="block text-sm text-gray-300 mb-1">Bank Category</label>
              <Select
                isMulti
                options={bankCategoriesOptions}
                value={formData?.selectedCategories}
                onChange={(selected) => handleMultiSelect(selected, bankCategoriesOptions, "selectedCategories")}
                placeholder="Select Category"
                noOptionsMessage={() => "No results found"}
                styles={customSelectStyles}
                className="text-sm"
              />
            </div>

            {/* Bank */}
            <div>
              <label className="block text-sm text-gray-300 mb-1">Bank</label>
              <Select
                isMulti
                options={banksOptions}
                value={formData?.selectedBanks}
                onChange={(selected) => handleMultiSelect(selected, banksOptions, "selectedBanks")}
                placeholder="Select Bank"
                noOptionsMessage={() => "No results found"}
                styles={customSelectStyles}
                className="text-sm"
              />
            </div>

            {/* Banking Representative */}
            <div>
              <label className="block text-sm text-gray-300 mb-1">Banking Representative</label>
              <Select
                isMulti
                options={partnersOptions}
                value={formData?.selectedPartners}
                onChange={(selected) => handleMultiSelect(selected, partnersOptions, "selectedPartners")}
                placeholder="Select Partner"
                noOptionsMessage={() => "No results found"}
                styles={customSelectStyles}
                className="text-sm"
              />
            </div>
          </div>
        ) : (
          <div className="mb-4">
            <label className="block text-sm text-gray-300 mb-1">Bank Name</label>
            <input
              type="text"
              value={formData?.bankName || ""}
              onChange={(e) => updateFormData("bankName", e.target.value)}
              placeholder="Enter bank name"
              className="w-full px-3 py-2 bg-[#1e1e1e] border border-[#333333] text-white rounded-md placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-yellow-400"
            />
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="mb-6">
        <label className="block text-sm text-gray-300 mb-1">Add Note</label>
        <textarea
          value={formData?.note || ""}
          onChange={(e) => updateFormData("note", e.target.value)}
          placeholder="Enter a note"
          className="w-full px-3 py-2 bg-[#1e1e1e] border border-[#333333] text-white rounded-md placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-yellow-400 min-h-[80px]"
        />
      </div>
    </div>
  );
};

export default AmountApprove;
